import { decodeJWT } from '../../utils/jwtUtils';

export interface McpServer {
  id: string;
  name: string;
  type?: string;
  description?: string;
  enabled?: boolean;
}

export interface CatalogTool {
  id: string;
  mcp_server_name?: string;
  mcp_server_id?: string;
}

export interface TenantTool {
  capability_id: string;
  name: string;
  description?: string;
  kind?: string;
  visibility?: McpToolVisibility;
  global_enabled?: boolean;
  tenant_enabled: boolean;
  has_override?: boolean;
  requires_binding?: boolean;
}

export interface CapabilityBinding {
  id: string;
  capability_id: string;
  tenant_id: string;
  advisor_id?: string | null;
  payload_template?: unknown;
  enabled: boolean;
  scope?: string;
  created_by?: string;
  updated_at?: string;
}

export type McpToolVisibility = 'internal' | 'external';

export interface McpCapabilityParameter {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  default?: unknown;
}

export interface McpCapability {
  capability_id: string;
  name: string;
  description: string;
  kind?: string;
  visibility: McpToolVisibility;
  requires_binding?: boolean;
  global_enabled?: boolean;
  http_method?: string;
  api_url?: string;
  parameters: McpCapabilityParameter[];
  payload_template?: unknown;
}

export interface CapabilityAssignment {
  id: string;
  capability_id: string;
  tenant_id: string;
  advisor_id?: string | null;
  payload_template?: unknown;
  enabled: boolean;
  scope?: string;
  created_by?: string;
  updated_at?: string;
}

export interface McpServersResponse {
  servers?: McpServer[];
}

export interface McpCatalogToolsResponse {
  tools?: CatalogTool[];
}

export interface TenantToolsListResponse {
  tenant_id?: string;
  tools?: TenantTool[];
}

export interface TenantBindingsListResponse {
  bindings?: CapabilityBinding[];
}

export interface AdvisorAssignmentsListResponse {
  assignments?: CapabilityAssignment[];
}

export interface SetAdvisorAssignmentsResponse {
  assignments?: CapabilityAssignment[];
  added?: string[];
  removed?: string[];
  failed?: string[];
}

export interface ToggleTenantToolRequest {
  tenantId: string;
  capabilityId: string;
  enabled: boolean;
}

export interface ToggleMcpServerRequest {
  mcp_server_id: string;
  active: boolean;
}

export interface ToggleMcpRequest {
  active: boolean;
}

export interface McpTenantSetting {
  tenant_id: string;
  mcp_server_id: string | null;
  mcp_active: boolean;
  updated_at?: string;
  updated_by?: string;
}

export interface ToggleMcpResponse {
  tenant_id: string;
  active: boolean;
  setting: McpTenantSetting;
}

export interface TenantBindingsListRequest {
  tenantId: string;
  advisorId?: string;
}

export interface CapabilityBindingGetRequest {
  tenantId: string;
  capabilityName: string;
  /** Required for advisor-scoped MCP tools so enabled state matches the advisor binding. */
  advisorId?: string;
}

export interface CapabilityBindingGetResponse {
  binding?: CapabilityBinding;
  tenant_enabled?: boolean;
  input_schema?: Record<string, unknown>;
  capability?: McpCapability;
}

export interface AdvisorAssignmentsListRequest {
  tenantId: string;
  advisorId: string;
}

export interface SetAdvisorAssignmentsRequest {
  tenantId: string;
  advisorId: string;
  capabilityIds: string[];
}

export interface ActionToolInputSchemaItem {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  default?: unknown;
}

export interface UpsertMcpToolRequest {
  /** Used for RTK cache invalidation only; not sent in the upsert request body. */
  tenantId?: string;
  tenant_id?: string;
  tool_type: string;
  name: string;
  description: string;
  requires_binding?: boolean;
  enabled: boolean;
  visibility?: McpToolVisibility;
  advisor_id?: string;
  aiworker_id?: string;
  payload_template?: Record<string, unknown>;
  inputSchema?: ActionToolInputSchemaItem[];
}

export interface UpdateMcpToolRequest {
  /** Used for RTK cache invalidation only; not sent in the update request body. */
  tenantId?: string;
  capability_name: string;
  description: string;
  visibility: McpToolVisibility;
  parameters: McpCapabilityParameter[];
}

/** @deprecated Use UpsertMcpToolRequest */
export type UpsertActionToolRequest = UpsertMcpToolRequest;

export interface UpsertActionToolResponse {
  created?: boolean;
  tool?: { name?: string; kind?: string };
}

/** Mirrors cty-mcp-server doc-frontend tenant id resolution from JWT. */
export function resolveTenantIdFromToken(token: string): string {
  const claims = decodeJWT(token);
  if (!claims) return '';
  const c = claims as Record<string, string | undefined>;
  return String(c.tenant_id || c.org_id || c.org_id__C || '');
}
